import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { BASE_API_PATH, FEATURED_COLLECTION_ID, PHOTOS_PATH } from '../constants';

type PhotoCategory = 'Travel' | 'Nature' | 'Cities' | 'Life';
type FilterOption = 'All Collections' | PhotoCategory;

interface PhotoCollectionSummary {
    id: string;
    title: string;
    description: string;
    category: PhotoCategory;
    location: string;
    publishedAt: number;
    photoCount: number;
    coverImageKey: string;
    coverImageUrl: string;
    coverFocalPointX?: number;
    coverFocalPointY?: number;
}

interface GetPhotoCollectionsResponse {
    items: PhotoCollectionSummary[];
}

const FILTER_OPTIONS: FilterOption[] = ['All Collections', 'Travel', 'Life'];
const MIN_COVER_SKELETON_MS = 180;

function formatCollectionDate(timestamp: number) {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

export function Photos() {
    const [selectedFilter, setSelectedFilter] = useState<FilterOption>('All Collections');
    const [collections, setCollections] = useState<PhotoCollectionSummary[]>([]);
    const [loadingCollections, setLoadingCollections] = useState(true);
    const [collectionError, setCollectionError] = useState(false);
    const [loadedCoverImageKeys, setLoadedCoverImageKeys] = useState<Record<string, boolean>>({});
    const coverLoadStartTimesRef = useRef<Record<string, number>>({});
    const coverLoadTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    const getCoverImageLoadKey = (collection: PhotoCollectionSummary) =>
        `${collection.id}-${collection.coverImageKey || 'cover'}-${collection.coverImageUrl}`;

    const markCoverImageAsLoaded = (coverImageLoadKey: string) => {
        const loadStartedAt = coverLoadStartTimesRef.current[coverImageLoadKey] ?? Date.now();
        const elapsed = Date.now() - loadStartedAt;
        const remainingDelay = Math.max(0, MIN_COVER_SKELETON_MS - elapsed);
        if (coverLoadTimeoutsRef.current[coverImageLoadKey]) {
            return;
        }
        coverLoadTimeoutsRef.current[coverImageLoadKey] = setTimeout(() => {
            setLoadedCoverImageKeys((previous) =>
                previous[coverImageLoadKey] ? previous : { ...previous, [coverImageLoadKey]: true },
            );
            delete coverLoadTimeoutsRef.current[coverImageLoadKey];
        }, remainingDelay);
    };

    useEffect(() => {
        document.title = 'Photos - Mike Brauninger';
        ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
    }, []);

    useEffect(() => {
        const fetchCollections = async () => {
            try {
                const response = await fetch(`${BASE_API_PATH}/photos`);
                if (!response.ok) {
                    throw new Error('Could not fetch photo collections');
                }
                const data: GetPhotoCollectionsResponse = await response.json();
                setCollections(data.items || []);
                setCollectionError(false);
            } catch (error) {
                setCollectionError(true);
            } finally {
                setLoadingCollections(false);
            }
        };

        fetchCollections();
    }, []);

    const filteredCollections = useMemo(() => {
        const nonFeaturedCollections = collections.filter(
            (collection) => collection.id !== FEATURED_COLLECTION_ID,
        );
        if (selectedFilter === 'All Collections') {
            return nonFeaturedCollections;
        }
        return nonFeaturedCollections.filter((collection) => collection.category === selectedFilter);
    }, [selectedFilter, collections]);

    useEffect(() => {
        const now = Date.now();
        filteredCollections.forEach((collection) => {
            const coverImageLoadKey = getCoverImageLoadKey(collection);
            if (!coverLoadStartTimesRef.current[coverImageLoadKey]) {
                coverLoadStartTimesRef.current[coverImageLoadKey] = now;
            }
        });
    }, [filteredCollections]);

    useEffect(() => {
        return () => {
            Object.values(coverLoadTimeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>Photos - Mike Brauninger</title>
            </Helmet>
            <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 } }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h3" sx={{ color: '#163f86', fontWeight: 700, mb: 1 }}>
                            Photos
                        </Typography>
                        <Typography sx={{ color: '#55729f', maxWidth: 520 }}>
                            Moments from my travels and everyday life.
                        </Typography>
                    </Box>

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            mb: 2.5,
                            pb: 0.5,
                            justifyContent: { xs: 'center', md: 'flex-start' },
                            flexWrap: 'wrap',
                            rowGap: 1,
                        }}
                    >
                        {FILTER_OPTIONS.map((filter) => {
                            const isSelected = selectedFilter === filter;
                            return (
                                <Chip
                                    key={filter}
                                    label={filter}
                                    onClick={() => setSelectedFilter(filter)}
                                    sx={{
                                        backgroundColor: isSelected ? '#2d66c3' : '#eef5ff',
                                        color: isSelected ? '#ffffff' : '#325887',
                                        '& .MuiChip-label': {
                                            fontWeight: isSelected ? 700 : 500,
                                        },
                                        '&.MuiChip-clickable:hover': {
                                            backgroundColor: isSelected ? '#2d66c3' : '#eef5ff',
                                        },
                                        '&.Mui-focusVisible': {
                                            backgroundColor: isSelected ? '#2d66c3' : '#eef5ff',
                                        },
                                        '&:active': {
                                            backgroundColor: isSelected ? '#2d66c3' : '#eef5ff',
                                        },
                                    }}
                                />
                            );
                        })}
                    </Stack>

                    {loadingCollections ? (
                        <Typography sx={{ color: '#55729f' }}>Loading photo collections...</Typography>
                    ) : collectionError ? (
                        <Typography sx={{ color: '#55729f' }}>Unable to load photo collections right now.</Typography>
                    ) : (
                        <Grid container spacing={2}>
                            {filteredCollections.map((collection) => (
                                <Grid item xs={12} md={6} lg={4} key={collection.id}>
                                    {(() => {
                                        const coverImageLoadKey = getCoverImageLoadKey(collection);
                                        const isCoverImageLoaded = Boolean(loadedCoverImageKeys[coverImageLoadKey]);
                                        return (
                                    <Box
                                        component={Link}
                                        to={`/${PHOTOS_PATH}/${collection.id}`}
                                        sx={{
                                            border: '1px solid #d9e7f8',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            backgroundColor: '#f8fbff',
                                            height: '100%',
                                            textDecoration: 'none',
                                            display: 'block',
                                            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0px 10px 24px rgba(16, 56, 114, 0.12)',
                                            },
                                        }}
                                    >
                                        <Box sx={{ position: 'relative', width: '100%', pt: '62.5%' }}>
                                            {!isCoverImageLoaded && (
                                                <Skeleton
                                                    variant="rectangular"
                                                    animation="wave"
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'block',
                                                        bgcolor: '#e7f0fd',
                                                    }}
                                                />
                                            )}
                                            <Box
                                                component="img"
                                                src={collection.coverImageUrl}
                                                alt={collection.title}
                                                onLoad={() => markCoverImageAsLoaded(coverImageLoadKey)}
                                                onError={() => markCoverImageAsLoaded(coverImageLoadKey)}
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    objectPosition: `${collection.coverFocalPointX ?? 50}% ${collection.coverFocalPointY ?? 50}%`,
                                                    opacity: isCoverImageLoaded ? 1 : 0,
                                                    transition: 'opacity 160ms ease',
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ p: 1.75 }}>
                                            <Typography sx={{ color: '#163f86', fontWeight: 700, mb: 0.5 }}>
                                                {collection.title}
                                            </Typography>
                                            <Typography sx={{ color: '#55729f', fontSize: 14, mb: 1.25 }}>
                                                {collection.description}
                                            </Typography>
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                sx={{
                                                    color: '#55729f',
                                                    fontSize: 12.5,
                                                    fontFamily: (theme) => theme.typography.fontFamily,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                    <CollectionsOutlinedIcon sx={{ fontSize: 16 }} />
                                                    <span>{collection.photoCount} photos</span>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                    <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
                                                    <span>{collection.location}</span>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                    <TodayOutlinedIcon sx={{ fontSize: 16 }} />
                                                    <span>{formatCollectionDate(collection.publishedAt)}</span>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Box>
                                        );
                                    })()}
                                </Grid>
                            ))}
                        </Grid>
                    )}

                </Box>
            </Box>
        </>
    );
}
